import React from 'react';
import { useNavigate } from 'react-router';
import { Stack, TableHead, Typography, Chip } from '@mui/material';
import {
  ActionsTableRow,
  CustomTable,
  CustomTableBody,
  CustomTableContainer,
  CustomTableHeadCell,
  CustomTableRow,
} from 'src/components/MainPage/_shared/Table';
import { useQuery } from '@tanstack/react-query';
import {
  fetchAllWarehouseIncomes,
  WarehouseIncomeStatus,
  WarehouseIncomeType,
} from './services';
import { useSearch } from './Search';
import { useWarehouseIncomesData } from './hooks/useWarehouseIncomesData';
import { getEnumDescription, formatDocumentDate } from './helpers';
import { TextTruncated } from 'src/uikit/TextTruncated';

const columns = [
  { label: 'Документ' },
  { label: 'Склад' },
  { label: 'Поставщик' },
  { label: 'Комментарий' },
];

const MAX_CELL_WIDTH = 200;
const MAX_CELL_SHORT_WIDTH = 120;

export const WarehouseIncomesTable = ({ isReadOnly = false }) => {
  const navigate = useNavigate();
  const {
    params: { supplierId, warehouseId, type, status, dateFrom, dateTo },
  } = useSearch();
  const { types } = useWarehouseIncomesData();

  const { data, isLoading } = useQuery({
    queryKey: [
      'warehouse-incomes',
      supplierId,
      warehouseId,
      type,
      status,
      dateFrom,
      dateTo,
    ],
    queryFn: () =>
      fetchAllWarehouseIncomes({
        supplierId: supplierId || undefined,
        warehouseId: warehouseId || undefined,
        type: type ? (Number(type) as WarehouseIncomeType) : undefined,
        status: status ? (Number(status) as WarehouseIncomeStatus) : undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      }),
    refetchOnWindowFocus: false,
  });

  const hasFilters = Boolean(
    supplierId || warehouseId || type || status || dateFrom || dateTo
  );
  const showActions = !(isReadOnly || hasFilters);

  return (
    <CustomTableContainer data-testid="warehouse-incomes_table">
      <CustomTable
        stickyHeader
        fullHeight={!data?.length}
        sx={{
          tableLayout: 'auto',
          '& .MuiTableCell-root': {
            whiteSpace: 'nowrap',
            verticalAlign: 'middle',
          },
          '& .MuiTableBody-root .MuiTableCell-root': {
            height: '5rem',
          },
        }}
      >
        <TableHead>
          <CustomTableRow withDivider keepSticky>
            {columns.map(({ label }, index) => (
              <CustomTableHeadCell
                key={label}
                colSpan={index === columns.length - 1 ? 2 : 1}
              >
                {label}
              </CustomTableHeadCell>
            ))}
          </CustomTableRow>
        </TableHead>

        <CustomTableBody loading={isLoading} noItemsText="Нет данных">
          {data?.map((income) => {
            const typeDescription = getEnumDescription(types, income.type);

            const documentCell = (
              <Stack gap={0.5}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Chip
                    onClick={() => navigate(String(income.id))}
                    title="Смотреть детали"
                    label={income.number}
                    sx={{
                      borderRadius: 1,
                      backgroundColor: 'text.disabled',
                      color: 'secondary.contrastText',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: 'text.secondary',
                      },
                    }}
                  />
                  <Typography
                    variant="body2"
                    color="text.disabled"
                    sx={{ textDecoration: 'none' }}
                  >
                    от {formatDocumentDate(income.createdAt)}
                  </Typography>
                </Stack>
                <Typography variant="body2">{typeDescription}</Typography>
              </Stack>
            );

            const rowsData = [
              documentCell,
              <TextTruncated
                key="warehouse"
                text={income.warehouse?.name ?? '—'}
                maxWidth={MAX_CELL_SHORT_WIDTH}
              />,
              <TextTruncated
                key="supplier"
                text={income.supplier?.name ?? '—'}
                maxWidth={MAX_CELL_SHORT_WIDTH}
              />,
              <TextTruncated
                key="comment"
                text={income.comment || '—'}
                maxWidth={MAX_CELL_WIDTH}
              />,
            ];

            return (
              <ActionsTableRow
                key={income.id}
                rowsData={rowsData}
                noMutationAction={!showActions}
                onNavigate={() => navigate(String(income.id))}
                isRowUpdating={isLoading}
                data-testid="warehouse-income_row"
              />
            );
          })}
        </CustomTableBody>
      </CustomTable>
    </CustomTableContainer>
  );
};
